/**
 * apps/wildswim-web/src/lib/apiClient.ts
 *
 * Browser-side HTTP client for the wildswim-api backend.
 *
 * Plan §20.6 — pattern matches apps/web/src/lib/queryClient pattern.
 * The wildswim-api enforces App Check on every /v1/* request; for
 * the web client we rely on the reCAPTCHA-v3 App Check provider
 * registered at boot in `firebase.ts`. The Firebase SDK injects
 * the X-Firebase-AppCheck header automatically on `fetch` calls
 * routed through `getToken()`.
 *
 * Auth: when the user is signed in (anonymous OR Apple/Google),
 * we attach the Firebase ID token as Bearer for /v1/me/* calls.
 *
 * Errors are surfaced as `ApiClientError` so react-query can
 * display them via the standard error-boundary path.
 */

import { auth } from './firebase';
import { getAppCheckToken } from './firebase';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

/**
 * Shareable verdict card (June 2026) — absolute URL of the dynamic
 * OG image the API renders for a spot. Used in <meta og:image> tags,
 * which unfurl bots resolve with no base URL, so this must always be
 * absolute even when API_BASE is a relative dev proxy path.
 */
const PUBLIC_API_FALLBACK = 'https://wildswim-api-staging-367571697739.europe-west2.run.app';
export function spotOgCardUrl(spotId: string): string {
    const base = API_BASE.startsWith('http') ? API_BASE : PUBLIC_API_FALLBACK;
    return `${base}/v1/spots/${encodeURIComponent(spotId)}/og-card.png`;
}

export class ApiClientError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
    ) {
        super(message);
    }
}

interface ErrorEnvelope {
    error?: { code?: string; message?: string };
}

interface DataEnvelope<T> {
    data: T;
}

async function buildHeaders(extra?: HeadersInit): Promise<Headers> {
    const headers = new Headers(extra);
    headers.set('Accept', 'application/json');

    // App Check token (reCAPTCHA v3) — required by every public
    // /v1/* route. Backend's requireAppCheck middleware rejects
    // requests without it.
    const appCheck = await getAppCheckToken();
    if (appCheck) headers.set('X-Firebase-AppCheck', appCheck);

    // Firebase Auth bearer for /v1/me/* + optional auth on /v1/planner.
    const user = auth.currentUser;
    if (user) {
        const idToken = await user.getIdToken();
        headers.set('Authorization', `Bearer ${idToken}`);
    }

    return headers;
}

async function request<T>(
    path: string,
    init: RequestInit = {},
): Promise<T> {
    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
    const headers = await buildHeaders(init.headers);
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    const res = await fetch(url, { ...init, headers });
    const text = await res.text();
    let body: unknown = null;
    try { body = text.length > 0 ? JSON.parse(text) : null; } catch { /* fall through */ }

    if (!res.ok) {
        const env = body as ErrorEnvelope | null;
        throw new ApiClientError(
            env?.error?.message ?? `HTTP ${res.status}`,
            res.status,
            env?.error?.code,
        );
    }

    // Backend always wraps successful payloads in { data, meta? }.
    const env = body as DataEnvelope<T> | T | null;
    if (env && typeof env === 'object' && 'data' in (env as object)) {
        return (env as DataEnvelope<T>).data;
    }
    return env as T;
}

export const api = {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) =>
        request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
    put: <T>(path: string, body: unknown) =>
        request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
    patch: <T>(path: string, body: unknown) =>
        request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
    /**
     * DELETE. Optional body — most DELETEs don't carry one, but
     * `/v1/me/account` does (Plan §26: lets the user pick the
     * contribution-deletion mode). Express + middleware accept
     * DELETE-with-body the same way as POST/PUT.
     */
    delete: <T>(path: string, body?: unknown) =>
        request<T>(
            path,
            body === undefined
                ? { method: 'DELETE' }
                : { method: 'DELETE', body: JSON.stringify(body) },
        ),
};
